import { useEffect } from 'react';
import {
    UseFormClearErrors,
    UseFormRegister,
    UseFormSetValue,
    useFormContext,
} from 'react-hook-form';
import { Box, List, TextInput, Typography } from '@vp/swan';
import { __ } from '@99designs/i18n';
import { useFieldViewSkinContext } from '../../../context/FieldViewContext';
import { useBriefFormContext } from '../../BriefContext/BriefFormContext';
import { FormInput } from '../BriefForm';
import { useFieldProcessing } from '../FieldProcessingProvider';
import { SubText } from '../SubText';
import { Field_MultiInputField_Fragment } from '../brief.generated';
import { useDebounceMultiTextUpdate } from './text/useDebouncedTextUpdate';
import { useUpdateMultiTextField } from './useUpdateMultiTextField';

export type MultiInputFieldProps = Field_MultiInputField_Fragment & {
    numberOfInputs?: number;
    register: UseFormRegister<any>;
    setValue: UseFormSetValue<any>;
    clearErrors: UseFormClearErrors<FormInput>;
};

export function Input({
    id,
    required,
    placeholder,
    multiInputValue,
    register,
    numberOfInputs = 3,
    clearErrors,
    setValue,
    subtext,
    errorMessage,
}: MultiInputFieldProps) {
    useEffect(() => {
        // react-hook-form will set the default value as empty strings which ends up populating
        // the db with empty strings. Although this is valid it is unnecessary.
        if (multiInputValue.length) {
            multiInputValue.forEach((v, i) => setValue(`${id}.${i}`, v));
        } else {
            setValue(id, []);
        }
    }, []);

    if (required) {
        // There are no obvious solutions on how to handle required fields with react-hook-form
        // for field sets. This is a temporary solution since there are no required fields
        // for the logo-essential product.
        throw new Error('MultiInputField does not support required fields');
    }

    return (
        <div role="group">
            {[...Array(numberOfInputs).keys()].map((i) => {
                return (
                    <InnerMultiInputField
                        errorMessage={errorMessage}
                        key={`${id}-${i}`}
                        id={id}
                        multiInputValue={multiInputValue}
                        i={i}
                        placeholder={placeholder}
                        required={required}
                        register={register}
                        setValue={setValue}
                        clearErrors={clearErrors}
                        numberOfInputs={numberOfInputs}
                    />
                );
            })}
            {subtext && <SubText>{subtext}</SubText>}
        </div>
    );
}

function InnerMultiInputField({
    id,
    i,
    multiInputValue,
    placeholder,
    required,
    register,
    setValue,
    clearErrors,
    numberOfInputs = 3,
    errorMessage,
}: {
    id: string;
    i: number;
} & Pick<
    MultiInputFieldProps,
    | 'multiInputValue'
    | 'placeholder'
    | 'required'
    | 'register'
    | 'setValue'
    | 'clearErrors'
    | 'numberOfInputs'
    | 'errorMessage'
>) {
    const innerId = `${id}.${i}`;
    const isLastInput = i === numberOfInputs - 1;
    const { briefId } = useBriefFormContext();
    const updateMutation = useUpdateMultiTextField(id, briefId, multiInputValue);
    const { setProcessingField } = useFieldProcessing();
    const debouncedTextUpdate = useDebounceMultiTextUpdate(innerId, updateMutation);
    const { getValues } = useFormContext<FormInput>();
    const errorMsg = errorMessage || __('This field is required');
    return (
        <Box key={`${id}-${i}`} marginBottom={isLastInput ? 0 : '6'}>
            <TextInput
                id={innerId}
                {...(multiInputValue && multiInputValue[i]
                    ? { defaultValue: multiInputValue[i] }
                    : {})}
                placeholder={placeholder ?? ''}
                {...register(innerId, {
                    required: required ? errorMsg : false,
                    onChange: (e) => {
                        setValue(innerId, e.target.value);
                        clearErrors(innerId);
                        if (required && (e.target.value == null || e.target.value === '')) {
                            return;
                        }
                        setProcessingField(innerId, true);
                        const updatedValues = [...(getValues(id) ?? [])];
                        updatedValues[i] = e.target.value ?? '';
                        debouncedTextUpdate(updatedValues);
                    },
                })}
            />
        </Box>
    );
}

type MultiInputFieldViewProps = {
    multiInputValue: Field_MultiInputField_Fragment['multiInputValue'];
};

export function View({ multiInputValue: urls }: MultiInputFieldViewProps) {
    const skin = useFieldViewSkinContext();

    return (
        <Typography fontSize={skin.fontSize}>
            <List>
                {urls.map((url) => (
                    <li key={url}>{url}</li>
                ))}
            </List>
        </Typography>
    );
}
